import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, XIcon } from "@heroicons/react/outline";
import { capitalize } from "lodash";
import React, { Fragment, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { ModalUpgradeConfig } from "../../context/toast/ToastProvider";
import { PlanType } from "../../utils/types";

interface Props {
  config: ModalUpgradeConfig;
  setConfig: React.Dispatch<React.SetStateAction<ModalUpgradeConfig>>;
}

const ModalUpgrade: React.FC<Props> = ({ config, setConfig }) => {
  const onClose = useCallback(() => {
    setConfig({ ...config, open: false });
  }, [config, setConfig]);
  const history = useHistory();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const onTrigger = useCallback(
    (type: "cancel" | "resolve", button?: string) => {
      const { resolve } = config;

      if (type === "resolve" && resolve) {
        resolve(button);
      }

      onClose();
    },
    [config, onClose]
  );

  return (
    <Transition.Root show={config.open} as={Fragment}>
      <Dialog
        as="div"
        open={config.open}
        className="fixed z-50 inset-0 overflow-y-auto"
        onClose={() => onTrigger("cancel")}
        initialFocus={cancelButtonRef}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block relative align-bottom bg-white rounded-lg px-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full dark:bg-gray-900">
              <img
                src="/molecule2.png"
                className="absolute left-0 top-0 z-0"
                alt="molecule"
              />
              <div className="block absolute top-0 right-0 pt-4 pr-4 z-10">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                  onClick={() => onTrigger("cancel")}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row relative">
                <div className="flex flex-col justify-start items-start w-full sm:w-8/12 pt-16 pb-10 px-2 sm:px-10">
                  <span className="uppercase text-xs text-hotspotty-400 dark:text-hotspotty-300 font-semibold">
                    Your next step: {config.nextPlan?.type ?? PlanType.pro} plan
                  </span>

                  {config.featureTitle ? (
                    <div className="flex flex-col">
                      <span className="text-3xl font-bold dark:text-white mt-2">
                        {config.featureTitle}
                      </span>
                      <span className="text-base font-light dark:text-gray-300 mt-1">
                        {config.featureDescription}
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className="text-3xl font-bold dark:text-white mt-2">
                        You are missing out on the real deal!
                      </span>
                      <span className="text-base font-light dark:text-gray-300 mt-3">
                        Get access to powerful features and add more hotspots,
                        locations, team members, and super simulations to your
                        workspace.
                      </span>
                    </>
                  )}

                  <img
                    src="/upgrade-plan-cover.svg"
                    className="-ml-5 mt-6 z-10"
                    alt="pro plan"
                  />

                  {!config.featureTitle && (
                    <span className="text-sm font-light dark:text-gray-300 mt-2 mb-4">
                      The community plan has a limit of 1 hotspot, so you will
                      not see more than 1 hotspot in your workspace. You can
                      upgrade your plan to get access to more hotspots and
                      features.
                    </span>
                  )}

                  <button
                    onClick={() => {
                      onTrigger("resolve");
                      history.push("/pricing");
                    }}
                    className="text-xs w-full sm:w-auto text-white py-2 px-8 shadow rounded-md outline-none transition-all bg-hotspotty-400 border-hotspotty-400 hover:bg-hotspotty-400 dark:hover:bg-hotspotty-500 focus:outline-none"
                  >
                    Upgrade
                  </button>
                </div>

                <div className="flex flex-col w-full sm:w-4/12 border-t sm:border-t-0 sm:border-l dark:border-gray-500 px-2 sm:px-10 pt-4 sm:pt-16 pb-10">
                  <span className="font-bold text-xl dark:text-white">
                    {config.nextPlan?.type
                      ? capitalize(config.nextPlan.type)
                      : PlanType.pro}{" "}
                    feature plan
                  </span>
                  <div className="dark:text-gray-300 flex items-center">
                    <span className="text-sm">From $</span>
                    <span className="text-md font-bold">
                      {(config.nextPlan?.price || 0) / 100}
                    </span>
                    <span className="text-sm font-light dark:text-gray-300">/month</span>
                  </div>

                  {config.nextPlan?.features && (
                    <div className="flex flex-col mt-4">
                      {config.nextPlan.features.map((feature, index) => (
                        <span
                          key={feature}
                          className="flex items-center text-sm dark:text-gray-300 font-light my-2"
                        >
                          {index > 0 && (
                            <CheckIcon className="w-4 flex-shrink-0 text-green-400 mr-2" />
                          )}
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => {
                      onTrigger("resolve");
                      history.push("/pricing/compare-feature-plans");
                    }}
                    className="text-xs text-white py-2 px-8 mt-6 shadow rounded-md outline-none transition-all bg-gray-800 border-gray-800 hover:bg-gray-700 focus:outline-none"
                  >
                    Compare feature plans
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalUpgrade;
